/**
 * CENTRALIZED DOMICILIATION PERMISSIONS GUARD COMPONENT
 * 
 * This component controls the visibility of domiciliation UI based on permissions.
 * It can be used as a wrapper component or with render props.
 * 
 * Usage:
 * - Simple wrapper: <DomiciliationGuard token={token}><Component /></DomiciliationGuard>
 * - Render props: <DomiciliationGuard token={token}>{({ canView }) => ...}</DomiciliationGuard>
 * - Without token (cliente): <DomiciliationGuard><Component /></DomiciliationGuard>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDomiciliationPermission } from '../hooks/useDomiciliationPermission';

/**
 * Guard component for domiciliation UI
 */
function DomiciliationGuard({ 
  token = null, 
  children, 
  fallback = null, 
  noPermission = null,
  showLoading = true
}) {
  const { canView, isLoading, error, userType } = useDomiciliationPermission(token);

  // If children is a function, use render props pattern
  if (typeof children === 'function') {
    return children({ 
      canView, 
      isLoading, 
      error, 
      userType 
    });
  }

  // Show loading state
  if (isLoading && showLoading) {
    return fallback;
  }

  // No permission
  if (!canView) {
    return noPermission;
  }

  // Has permission, render children
  return children;
}

DomiciliationGuard.propTypes = {
  token: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func
  ]).isRequired,
  fallback: PropTypes.node,
  noPermission: PropTypes.node,
  showLoading: PropTypes.bool
};

export default DomiciliationGuard;




// WEBPACK FOOTER //
// ./src/components/DomiciliationGuard.js